import { useCallback, useEffect, useState, type AnimationEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { subscribeFirstInvoiceCelebration } from '../../lib/first-invoice-celebration.js';
import './first-invoice-celebration.css';

/**
 * Sceau de la première facture. Il se pose au-dessus du document, sans prendre les clics,
 * puis s'efface. `prefers-reduced-motion` laisse la feuille immobile, le temps d'une lecture.
 */

interface Celebration {
  readonly id: number;
  readonly number: string | null;
}

const CALM_MS = 1600;
const SAFETY_MS = 2800;
const LEAVE_ANIMATION = 'first-invoice-leave';

function prefersReducedMotion(): boolean {
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function Seal() {
  return (
    <svg className="first-invoice-seal" viewBox="0 0 72 72" aria-hidden="true">
      <circle
        cx="36"
        cy="36"
        r="32"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
        opacity="0.28"
      />
      <circle
        className="first-invoice-seal-ring"
        cx="36"
        cy="36"
        r="28"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        className="first-invoice-seal-mark"
        d="M24 37.5 32.5 46 49 27"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CelebrationView({
  number,
  onDone,
}: {
  number: string | null;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const reduced = prefersReducedMotion();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      onDone();
    }, reduced ? CALM_MS : SAFETY_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [onDone, reduced]);

  const finish = (event: AnimationEvent<HTMLDivElement>) => {
    if (reduced) return;
    if (event.target !== event.currentTarget) return;
    if (event.animationName !== LEAVE_ANIMATION) return;
    onDone();
  };

  return createPortal(
    <div
      className={
        reduced
          ? 'first-invoice-root pointer-events-none'
          : 'first-invoice-root first-invoice-play pointer-events-none'
      }
      data-testid="first-invoice-celebration"
      data-motion={reduced ? 'reduced' : 'animated'}
      role="status"
      aria-live="polite"
      onAnimationEnd={finish}
    >
      <div
        className={reduced ? 'first-invoice-veil first-invoice-veil-calm' : 'first-invoice-veil'}
        aria-hidden="true"
      />
      <div className="first-invoice-stage">
        {reduced ? null : (
          <>
            <span
              className="first-invoice-ring"
              data-testid="first-invoice-ring"
              aria-hidden="true"
            />
            <span className="first-invoice-ring first-invoice-ring-accent" aria-hidden="true" />
          </>
        )}
        <div className="first-invoice-sheet">
          <Seal />
          {number !== null && number !== '' ? (
            <p className="first-invoice-number" data-testid="first-invoice-number">
              {number}
            </p>
          ) : null}
          <p className="first-invoice-line">{t('billing:first_invoice.issued')}</p>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function FirstInvoiceCelebration() {
  const [celebration, setCelebration] = useState<Celebration | null>(null);
  const dismiss = useCallback(() => {
    setCelebration(null);
  }, []);

  useEffect(
    () =>
      subscribeFirstInvoiceCelebration((next) => {
        setCelebration(next);
      }),
    [],
  );

  if (celebration === null) return null;
  return (
    <CelebrationView
      key={celebration.id}
      number={celebration.number}
      onDone={dismiss}
    />
  );
}
